import { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useClients, Client } from '../context/ClientContext';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import CreateTaskSidepanel from '../components/clientDetail/sections/CreateTaskSidepanel';
import {
  ClientTable,
  CreateClientModal,
  EditClientSidepanel,
  ClientDetailPanel,
  ClientDetailBody,
  EmptyState,
  getAttention,
  isRecent,
  relativeDate,
  sortClients,
  CLIENT_SORTS,
  SERVICE_TABS,
  useClientTasks,
  useMediaQuery,
  BELOW_LG_QUERY,
  PHONE_QUERY,
} from '../components/clients';
import type { ClientSort, ServiceTab } from '../components/clients';
import { supabase } from '../lib/supabase';
import {
  Button,
  Card,
  Dropdown,
  Icon,
  PulseDot,
  SegmentedControl,
  StatCard,
  Tabs,
} from '../components/ui';

const VIEW_STORAGE_KEY = 'cockpit.clients.view';
type ClientsView = 'table' | 'focus';

function readStoredView(): ClientsView {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'table' ? 'table' : 'focus';
  } catch {
    return 'focus';
  }
}

/* ---------- focus view (list + live pane) ---------- */

interface FocusViewProps {
  rows: Client[];
  focusId: string | null;
  onFocus: (id: string | null) => void;
  onOpen: (client: Client) => void;
  onAddTask: (client: Client) => void;
  onEdit: (client: Client) => void;
}

function FocusView({ rows, focusId, onFocus, onOpen, onAddTask, onEdit }: FocusViewProps) {
  // Below lg the two columns stack, which would put the live pane under the whole list, out of sight of the row
  // that was tapped. There the pane opens inline under its row instead, and only once a row is tapped.
  const stacked = useMediaQuery(BELOW_LG_QUERY);
  const selected = stacked ? rows.find((c) => c.id === focusId) : rows.find((c) => c.id === focusId) || rows[0];
  const focusTasks = useClientTasks(selected?.id);

  // Opening a row collapses the one above it, which can carry the tapped row up off the screen.
  useEffect(() => {
    if (!stacked || !focusId) return;
    document.querySelector(`[data-focus-row="${focusId}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [stacked, focusId]);

  const pane = (client: Client) => (
    <ClientDetailBody
      client={focusTasks.length > 0 ? { ...client, tasks: focusTasks } : client}
      actions={
        <>
          <Button size="sm" variant="secondary" icon="plus" onClick={() => onAddTask(client)}>
            Add Task
          </Button>
          <Button size="sm" iconTrailing="arrow-right" nudge onClick={() => onOpen(client)}>
            Open Client
          </Button>
          <Button size="sm" variant="ghost" icon="pencil" onClick={() => onEdit(client)}>
            Edit
          </Button>
        </>
      }
    />
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 items-start" style={{ gap: 'var(--space-5)' }}>
      <div
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-rest)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: 'var(--surface-raised)',
            borderBottom: '1px solid var(--border-hairline)',
          }}
        >
          <span className="t-eyebrow">Client</span>
          <span className="t-eyebrow" style={{ marginRight: '26px' }}>Last Activity</span>
        </div>
        {rows.map((client, i) => {
          const att = getAttention(client);
          const isActive = selected && client.id === selected.id;
          return (
            <Fragment key={client.id}>
            <button
              data-focus-row={client.id}
              aria-expanded={stacked ? !!isActive : undefined}
              className="hover-wash pressable"
              onClick={() => onFocus(stacked && isActive ? null : client.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '11px',
                width: '100%',
                padding: '11px 14px',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                background: isActive ? 'var(--surface-active)' : 'transparent',
                borderBottom: i < rows.length - 1 ? '1px solid var(--border-hairline)' : 'none',
                fontFamily: 'var(--font-ui)',
              }}
            >
              <span style={{ minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                    color: 'var(--text-body)',
                  }}
                >
                  <span className="ck-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {client.name}
                  </span>
                  {att ? <PulseDot tone={att.tone} title={att.label} /> : null}
                </span>
                <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '1px' }}>
                  {client.status}
                </span>
              </span>
              <span
                className="t-num"
                style={{
                  fontSize: 'var(--text-sm)',
                  color: isRecent(client.lastActivityDate) ? 'var(--text-body)' : 'var(--text-faint)',
                  flexShrink: 0,
                }}
              >
                {relativeDate(client.lastActivityDate)}
              </span>
              <Icon
                name="chevron-right"
                size={14}
                style={{
                  color: isActive ? 'var(--text-muted)' : 'var(--text-faint)',
                  flexShrink: 0,
                  transform: stacked && isActive ? 'rotate(90deg)' : undefined,
                  transition: 'transform var(--dur-base) var(--ease-out)',
                }}
              />
            </button>
            {stacked && isActive ? (
              <div
                style={{
                  padding: 'var(--space-5) var(--space-4)',
                  background: 'var(--surface-raised)',
                  borderBottom: i < rows.length - 1 ? '1px solid var(--border-hairline)' : 'none',
                }}
              >
                {pane(client)}
              </div>
            ) : null}
            </Fragment>
          );
        })}
      </div>

      {selected && !stacked ? (
        <div style={{ position: 'sticky', top: 'var(--space-5)' }}>
          <Card padding="var(--space-6)">{pane(selected)}</Card>
        </div>
      ) : null}
    </div>
  );
}

/* ---------- the page ---------- */

export default function Clients() {
  const {
    statusFilter,
    setStatusFilter,
    fetchClients,
    searchTerm,
    setSearchTerm,
    clients,
    filteredClients,
    isLoading,
    selectedClient,
    setSelectedClient,
  } = useClients();
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sort, setSort] = useState<ClientSort>('Attention');
  const [view, setView] = useState<ClientsView>(readStoredView);
  const [focusId, setFocusId] = useState<string | null>(null);
  // Phones show the KPI cards three across, a third of the width each: their hints get the short form.
  const phone = useMediaQuery(PHONE_QUERY);

  // The tabs filter by service (?service=seo | google-ads). The shared status filter stays on All, so every
  // client is in the list and the tabs narrow it here.
  const serviceParam = new URLSearchParams(location.search).get('service');
  const serviceFilter: ServiceTab = serviceParam === 'seo' ? 'SEO' : serviceParam === 'google-ads' ? 'Google Ads' : 'All';
  const setServiceFilter = (tab: ServiceTab) =>
    navigate(tab === 'All' ? '/clients' : `/clients?service=${tab === 'SEO' ? 'seo' : 'google-ads'}`, { replace: true });

  useEffect(() => {
    if (statusFilter !== 'All') setStatusFilter('All');
  }, [statusFilter, setStatusFilter]);

  // Service flags by client id. Not on the shared Client shape, so read here; refreshed whenever the list is.
  const [services, setServices] = useState<Map<string, { seo: boolean; ads: boolean }>>(new Map());
  useEffect(() => {
    let live = true;
    supabase
      .from('clients')
      .select('id, service_seo, service_google_ads')
      .then(({ data, error }) => {
        if (error) {
          console.error('Error loading client services:', error);
          return;
        }
        if (live) setServices(new Map((data ?? []).map((c) => [c.id, { seo: c.service_seo, ads: c.service_google_ads }])));
      });
    return () => {
      live = false;
    };
  }, [clients]);

  const changeView = (next: string) => {
    const v: ClientsView = next === 'table' ? 'table' : 'focus';
    setView(v);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, v);
    } catch {
      // localStorage unavailable — view simply won't persist
    }
  };

  const visible = useMemo(() => {
    const inTab =
      serviceFilter === 'All'
        ? filteredClients
        : filteredClients.filter((c) => (serviceFilter === 'SEO' ? services.get(c.id)?.seo : services.get(c.id)?.ads));
    return sortClients(inTab, sort);
  }, [filteredClients, sort, serviceFilter, services]);

  const activeClientsCount = useMemo(
    () => clients.filter((client) => client.status === 'Active').length,
    [clients]
  );

  const kpis = useMemo(() => {
    let prospects = 0;
    let tasksToday = 0;
    let review = 0;
    let clientsAwaiting = 0;
    clients.forEach((client) => {
      if (client.status === 'Prospect') prospects += 1;
      tasksToday += client.tasksDueToday + client.tasksAwaitingReview;
      review += client.tasksAwaitingReview;
      if (client.tasksAwaitingReview > 0) clientsAwaiting += 1;
    });
    return { prospects, tasksToday, review, clientsAwaiting };
  }, [clients]);

  const openPreview = (client: Client) => {
    setSelectedClient(client);
    setPreviewOpen(true);
  };

  const openClient = (client: Client) => {
    setSelectedClient(client);
    navigate(`/clients/${client.id}`);
  };

  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [addTaskTarget, setAddTaskTarget] = useState<Client | null>(null);

  const addTaskClient = (client: Client) => {
    setAddTaskTarget(client);
    setAddTaskOpen(true);
  };

  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Client | null>(null);

  const editClient = (client: Client) => {
    setEditTarget(client);
    setEditOpen(true);
  };

  const clearFilters = () => {
    setStatusFilter('All');
    setSearchTerm('');
  };

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Clients"
          count={activeClientsCount}
          searchPlaceholder="Search clients..."
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          actions={
            <Button size="sm" icon="plus" style={{ marginLeft: '6px' }} onClick={() => setIsModalOpen(true)}>
              Add Client
            </Button>
          }
        />

        {/* KPI row */}
        {/* three across on phones too: stacked, they filled the whole first screen */}
        <div
          className="grid grid-cols-3 gap-2 md:gap-[16px]"
          style={{ marginBottom: 'var(--space-6)' }}
        >
          <StatCard
            label="Active Clients"
            value={String(activeClientsCount)}
            hint={phone ? `${kpis.prospects} prospects` : `${kpis.prospects} prospects in pipeline`}
          />
          <StatCard
            label="Tasks Today"
            value={String(kpis.tasksToday)}
            hint={phone ? `${kpis.review} to review` : `${kpis.review} awaiting review`}
          />
          <StatCard
            label="Awaiting Review"
            value={String(kpis.review)}
            hint={
              kpis.review > 0
                ? `${phone ? '' : 'across '}${kpis.clientsAwaiting} client${kpis.clientsAwaiting === 1 ? '' : 's'}`
                : 'all clear'
            }
          />
        </div>

        {/* toolbar: service tabs + sort + view */}
        <div
          className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between"
          style={{ marginBottom: 'var(--space-5)' }}
        >
          <Tabs
            tabs={[...SERVICE_TABS]}
            active={serviceFilter}
            onChange={(tab) => setServiceFilter(tab as ServiceTab)}
            style={{ flex: 1 }}
          />
          <div className="flex items-center" style={{ gap: '10px', paddingBottom: '6px' }}>
            <Dropdown
              size="sm"
              options={CLIENT_SORTS.map((s) => ({ value: s, label: `Sort: ${s}` }))}
              value={sort}
              onChange={(e) => setSort(e.target.value as ClientSort)}
            />
            <SegmentedControl
              options={[{ id: 'table', label: 'Table' }, { id: 'focus', label: 'Focus' }]}
              value={view}
              onChange={changeView}
            />
          </div>
        </div>

        {isLoading ? (
          <div
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-5)',
            }}
          >
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse"
                style={{
                  height: '40px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--surface-raised)',
                  marginBottom: i < 5 ? '10px' : 0,
                }}
              />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-rest)',
            }}
          >
            <EmptyState onClear={clearFilters} />
          </div>
        ) : view === 'focus' ? (
          <FocusView rows={visible} focusId={focusId} onFocus={setFocusId} onOpen={openClient} onAddTask={addTaskClient} onEdit={editClient} />
        ) : (
          <ClientTable clients={visible} onSelect={openPreview} />
        )}
      </main>

      {/* Client preview slide-over */}
      <ClientDetailPanel
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        client={selectedClient}
      />

      {/* Add client slide-over */}
      <CreateClientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={fetchClients}
      />

      {/* Edit client slide-over */}
      <EditClientSidepanel
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        client={editTarget}
        onSaved={fetchClients}
      />

      {/* Add task slide-over */}
      {addTaskTarget && (
        <CreateTaskSidepanel
          isOpen={addTaskOpen}
          onClose={() => setAddTaskOpen(false)}
          client={addTaskTarget}
          onTaskAdded={fetchClients}
        />
      )}
    </div>
  );
}
